/** @jsx jsx */

import React from "react";

import { jsx, SerializedStyles } from "@emotion/core";
import { formHelperStyles } from "./FormHelperStyles";
import { Typography, Select } from "antd";
import { Field, ErrorMessage, FieldProps } from "formik";
import { formHelperLabels as LABELS } from "./FormHelperLabels";

const { Text } = Typography;
const { Option } = Select;

interface Props {
  name: string;

  /**
   * NOTE: support for "string[]" will be removed
   */
  options: string[] | { [key: string]: string };

  /**
   *  by default "name" is used to indicate the field name in UI
   *  but if a custom label is to be shown this overrides "name"
   */
  label?: string;

  loading?: boolean;

  mandatory?: boolean;
  placeholder?: string;

  disable?: boolean;

  hideError?: boolean;

  customError?: string;

  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;

  className?: string;

  dropdownMatchSelectWidth?: boolean;

  /**
   * these props are not necessary for most cases!
   * in a case where a field needs a custom onChange,onBlur use this.
   * NOTE: providing this function will override the functions provided by formik.
   *       the custom functions wil have to update formik state on it's own
   */
  onChange?: (value: any) => void; //"any" since types from antd and formik does not match
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 *
 * @param {Props} props
 * @returns Customizable dropdown component with an option to search
 * to be used only within the context of formik.
 */

const FormDropdown: React.FC<Props> = (props: Props) => {
  const {
    name,
    customError,
    mandatory,
    label,
    hideError,
    customStyles,
    className,
    options,
    placeholder,
    loading,
    disable,
    dropdownMatchSelectWidth
  } = props;
  return (
    <div className={className} css={[formHelperStyles, customStyles]}>
      {label && (
        <label className="d-block">
          {label}
          <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
        </label>
      )}
      <Field name={name}>
        {({ form, field }: FieldProps) => {
          const onChange =
            props.onChange || ((value: any) => form.setFieldValue(name, value));
          const onBlur =
            props.onBlur ||
            (() => {
              form.setFieldTouched(name);
            });

          return (
            <Select
              dropdownMatchSelectWidth={
                dropdownMatchSelectWidth !== undefined
                  ? dropdownMatchSelectWidth
                  : true
              }
              dropdownClassName="select-dropdown-container"
              className="select-container"
              showArrow
              value={field.value || undefined} //Just to display placeholder if data is null
              onBlur={onBlur}
              onChange={onChange}
              placeholder={placeholder || label || name}
              loading={loading}
              showSearch
              filterOption={(
                input,
                option //to search the options when user is typing, ignoring case
              ) =>
                (option.props.children?.toString().toLowerCase() || "").indexOf(
                  input.toLowerCase()
                ) >= 0
              }
              disabled={disable}
            >
              {Array.isArray(options)
                ? options.map(item => (
                    <Option value={item} key={item}>
                      {item}
                    </Option>
                  ))
                : Object.keys(options).map(key => (
                    <Option value={options[key]} key={key}>
                      {key}
                    </Option>
                  ))}
            </Select>
          );
        }}
      </Field>
      {!hideError && (
        <ErrorMessage name={name}>
          {msg => (
            <Typography className="error">{customError || msg}</Typography>
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default FormDropdown;
